import { Utils } from '../utils';

// ================================================================ //
// -- Interfaces

interface TabbableOpts {
  includeContainer?: boolean; // checks "rootElement" itself for tabbability
  includeHidden?: boolean; // bypasses check for element visibility
  filterFn?: (node: Element) => boolean; // custom filter
}

interface IteratorOpts {
  filterFn?: (node: Element) => boolean;
  flatten?: boolean;
}

interface ScopedCandidates {
  scope: Element;
  candidates: Element[];
}

// ================================================================ //
// -- Variables

const focusableSelectors = [
  'button',
  'input',
  'select',
  'textarea',
  'a[href]',
  '[tabindex]',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  'details>summary:first-of-type',
  'details',
];

const focusableSelector = focusableSelectors.join(',');

// ================================================================ //
// -- Focus Candidates

const getCandidates = (
  elements: HTMLElement[],
  includeContainer: boolean,
  options: IteratorOpts = {}
): HTMLElement[] | ScopedCandidates[] => {
  const candidates = [];
  const elementsToCheck = [...elements];

  while (!!elementsToCheck.length) {
    const el = elementsToCheck.shift();

    // == Check if candidate is a slot
    if (el.tagName === 'SLOT') {
      const assigned = (el as HTMLSlotElement).assignedElements();
      const content = !!assigned.length
        ? Utils.toHtmlArray(assigned)
        : Utils.toHtmlArray(el.children);
      const nestedCandidates = getCandidates(content, true, options);

      if (options.flatten) {
        candidates.push(...nestedCandidates);
      } else {
        candidates.push({ scope: el, candidates: nestedCandidates });
      }
    } else {
      // == Check if candidate element should be included
      const matchesSelector = el.matches(focusableSelector);
      if (
        matchesSelector &&
        options.filterFn(el) &&
        (includeContainer || !elements.includes(el))
      ) {
        candidates.push(el);
      }
      // == Check if candidate element has Shadow DOM
      const shadowRoot = el.shadowRoot;
      if (!!shadowRoot) {
        const nestedCandidates = getCandidates(
          Utils.toHtmlArray(shadowRoot.children),
          true,
          options
        );

        if (options.flatten) {
          candidates.push(...nestedCandidates);
        } else {
          candidates.push({ scope: el, candidates: nestedCandidates });
        }
      } else {
        // == No Shadow DOM, so dig into Light DOM children
        elementsToCheck.unshift(...Utils.toHtmlArray(el.children));
      }
    }
  }

  return candidates;
};

// ================================================================ //
// -- Utility

/**
 * In Chrome, <details/>, <audio controls/> and <video controls/> elements get a default
 * `tabIndex` of -1 when the 'tabindex' attribute isn't specified in the DOM,
 * yet they are still part of the regular tab order; in FF, they get a default
 * `tabIndex` of 0; since Chrome still puts those elements in the regular tab
 * order, consider their tab index to be 0.
 * Also browsers do not return `tabIndex` correctly for contentEditable nodes;
 * so if they don't have a tabindex attribute specifically set, assume it's 0.
 *
 * isScope is positive for custom element with shadow root or slot that by default
 * have tabIndex -1, but need to be sorted by document order in order for their
 * content to be inserted in the correct position.
 * @param el
 * @param isScope
 */
const getTabIndex = (el: HTMLElement, isScope?: boolean) => {
  if (el.tabIndex < 0) {
    const isTargetElement =
      /^(AUDIO|VIDEO|DETAILS)$/.test(el.tagName) || el.isContentEditable;
    const noTabIndexAttr = isNaN(parseInt(el.getAttribute('tabindex'), 10));

    if ((isScope || isTargetElement) && noTabIndexAttr) {
      return 0;
    }
  }

  return el.tabIndex;
};

const isInput = (el: HTMLElement) => el.tagName === 'INPUT';
const isHiddenInput = (el: HTMLElement) =>
  isInput(el) && (el as HTMLInputElement).type === 'hidden';

const isDetailsWithSummary = (el: HTMLElement) => {
  return (
    el.tagName === 'DETAILS' &&
    Array.from(el.children).some((child) => child.tagName === 'SUMMARY')
  );
};

// TODO: ADD FUNCTION TO DETERMINE WHICH RADIO BUTTON WITHIN THE GROUP IS TABBABLE

/**
 * Form fields (nested) inside a disabled fieldset are not focusable/tabbable
 * unless they are in the first <legend> element of the top-most disabled
 * fieldset
 */
const isDisabledFromFieldset = (el: HTMLElement) => {
  if (/^(INPUT|BUTTON|SELECT|TEXTAREA)$/.test(el.tagName)) {
    let parentEl = el.parentElement;

    // check if "el" is contained in a disabled <fieldset>
    while (parentEl) {
      if (
        parentEl.tagName === 'FIELDSET' &&
        parentEl.hasAttribute('disabled')
      ) {
        return true;
      }
      parentEl = parentEl.parentElement;
    }
  }

  return false;
};

// ================================================================ //
// -- Display Check

const isHidden = (el: HTMLElement) => {
  // == Check if element is within a "closed" <details> element

  const isDirectSummary = el.matches('details > summary:first-of-type');
  const nodeUnderDetails = isDirectSummary ? el.parentElement : el;
  if (nodeUnderDetails.matches('details:not([open]) *')) {
    return true;
  }

  return !el.getClientRects().length;
  // return (el.offsetParent === null);
};

// ================================================================ //
// -- Sorting

const sortOrderedTabbables = (a, b) => {
  return a.tabIndex === b.tabIndex
    ? a.documentOrder - b.documentOrder
    : a.tabIndex - b.tabIndex;
};

/**
 *
 * @param candidates
 */
const sortByOrder = (candidates: HTMLElement[] | ScopedCandidates[]) => {
  const regularTabbables = [];
  const orderedTabbables = [];

  candidates.forEach((item, i) => {
    const isScope = !!item.scope;
    const element = isScope ? item.scope : item;
    const candidateTabIndex = getTabIndex(element, isScope);
    const elements = isScope ? sortByOrder(item.candidates) : element;

    if (candidateTabIndex === 0) {
      isScope
        ? regularTabbables.push(...elements)
        : regularTabbables.push(element);
    } else {
      orderedTabbables.push({
        documentOrder: i,
        tabIndex: candidateTabIndex,
        item: item,
        isScope: isScope,
        content: elements,
      });
    }
  });

  return orderedTabbables
    .sort(sortOrderedTabbables)
    .reduce((acc, sortable) => {
      sortable.isScope
        ? acc.push(...sortable.content)
        : acc.push(sortable.content);
      return acc;
    }, [])
    .concat(regularTabbables);
};

// ================================================================ //
// -- Focus & Tabbable Check

/**
 * "Filter" function used in tabbable() to determine if given element
 * has the ability to receive :focus.
 * @param element - the element to check for :focusability
 * @param includeHidden - if true, bypass element visibility check
 */
const nodeIsFocusable = (element: HTMLElement, includeHidden: boolean) => {
  switch (true) {
    case element.hasAttribute('disabled'):
    case isHiddenInput(element):
    case !includeHidden && isHidden(element):
    case isDetailsWithSummary(element):
    case isDisabledFromFieldset(element):
      return false;
    default:
      return true;
  }
};

/**
 * "Filter" function used in tabbable() to determine if given element
 * can receive :focus via tab navigation.
 * @param element - the element to check for tabbability
 * @param includeHidden - if true, bypass element visibility check
 */
const nodeIsTabbable = (element: HTMLElement, includeHidden: boolean) => {
  switch (true) {
    // case isNonTabbableRadio(el): // TODO: IMPLEMENT RADIO GROUP HANDLER
    case getTabIndex(element) < 0:
    case !nodeIsFocusable(element, includeHidden):
      return false;
    default:
      return true;
  }
};

// ================================================================ //
// -- Public Functions

/**
 * Returns all elements within the "rootElement" that can receive
 * :focus via tab navigation (i.e. the element has the ability to
 * receive :focus AND doesn't have anything preventing it from
 * receiving :focus such as being disabled, hidden, etc.)
 * @param rootElement - parent of elements to check for tabbability
 * @param options - customize scope / parameters of tabbability check
 */
export const tabbable = (
  rootElement: HTMLElement,
  options: TabbableOpts = {}
) => {
  const { includeContainer, includeHidden, filterFn } = options;
  const filter = (el: HTMLElement) =>
    nodeIsTabbable(el, includeHidden) && (!!filterFn ? filterFn(el) : true);
  const candidates = getCandidates([rootElement], includeContainer, {
    filterFn: filter,
    flatten: false,
  });

  return sortByOrder(candidates);
};

/**
 * Returns true if element provided can receive :focus via tab
 * navigation (i.e. the element has the ability to receive :focus
 * AND doesn't have anything preventing it from receiving :focus
 * such as being disabled, hidden, etc.).
 * @param element - the element to check for tabbability
 * @param includeHidden - if true, bypass element visibility check
 */
export const isTabbable = (
  element: HTMLElement,
  includeHidden: boolean = false
): boolean => {
  !element &&
    Utils.consoleWarn(
      { tagName: 'isTabbable()' } as HTMLElement,
      'Invalid element provided'
    );

  return !element || !element.matches(focusableSelector)
    ? false
    : nodeIsTabbable(element, includeHidden);
};
